﻿import { FaceUVHelper } from './FaceUVHelper.js';
import { QuadriLateralFrustumBuilder, QLFFaceNdx } from './QuadriLateralFrustumBuilder.js';

/**
 * Creates rim which goes on top of all four sides of the basket. The shape is created
 * using the BABYLON.MeshBuilder.ExtrudeShape function. 4/9/2023.
  * This class was developed as a part of the prototyping of our mesh basket here:
 * https://playground.babylonjs.com/#WVLDLM#35
*/
class BasketRimBuilder {
  /**
   * Creates the 2D profile of the rim if we made a vertical cut through it and looked at it
   * horizontally down the Z-axis. In our case this shape is vertical rectangle with rounded corners,
   * typically 10 mm wide and 2.5 mm high.
   */
  createRimShape(width, height, cornerRadius) {
    /* If width = 1.0, height = 0.25, and cornerRadius = 0.05, then:
    const myShape = [
            new BABYLON.Vector3(0.00, 0.05, 0),
            new BABYLON.Vector3(0.00, 0.20, 0),
            new BABYLON.Vector3(0.05, 0.25, 0),
            new BABYLON.Vector3(0.95, 0.25, 0),
            new BABYLON.Vector3(1.00, 0.20, 0),
            new BABYLON.Vector3(1.00, 0.05, 0),
            new BABYLON.Vector3(0.95, 0.00, 0),
            new BABYLON.Vector3(0.05, 0.00, 0),
    ];
    */
    return [
      new BABYLON.Vector3(0.00, cornerRadius, 0),
      new BABYLON.Vector3(0.00, height - cornerRadius, 0),
      new BABYLON.Vector3(cornerRadius, height, 0),
      new BABYLON.Vector3(width - cornerRadius, height, 0),
      new BABYLON.Vector3(width, height - cornerRadius, 0),
      new BABYLON.Vector3(width, cornerRadius, 0),
      new BABYLON.Vector3(width - cornerRadius, 0.00, 0),
      new BABYLON.Vector3(cornerRadius, 0.00, 0),
    ];
  }

  /**
   * Creates a path forming a rounded rectangle as seen vertically down the Y-axis. The 2D shape
   * created by the createRimShape function will be extruded along this path.
   */
  createRimPath(origin, width, depth, cornerRadius) {
    const x = origin.x;
    const y = origin.y;
    const z = origin.z;
    const path = [
      new BABYLON.Vector3(x, y, z + cornerRadius),
      new BABYLON.Vector3(x, y, z + depth - cornerRadius),
      new BABYLON.Vector3(x + cornerRadius, y, z + depth),
      new BABYLON.Vector3(x + width - cornerRadius, y, z + depth),
      new BABYLON.Vector3(x + width, y, z + depth - cornerRadius),
      new BABYLON.Vector3(x + width, y, z + cornerRadius),
      new BABYLON.Vector3(x + width - cornerRadius, y, z),
      new BABYLON.Vector3(cornerRadius, y, z),
    ];
    path.push(path[0]);
    return path;
  }

  /**
   * Creates and returns the mesh.
   * @param {string} name For display/debugging
   * @param {object} origin Object with properties (x, y, z) setting the of the left, bottom, front
   * position of the rim.
   * @param {object} dimensions Object with properties (basketWidth, basketDepth, yCornerRadius, edgeWidth,
   * edgeHeight, zCornerRadius).
   * @param {object} scene Scene object
   * @returns {object} Mesh
   */
  createMesh(name, origin, dimensions, scene) {
    // Shape profile in XY plane:
    const shape = this.createRimShape(dimensions.edgeWidth, dimensions.edgeHeight, dimensions.zCornerRadius);
    const path = this.createRimPath(origin, dimensions.basketWidth, dimensions.basketDepth, dimensions.yCornerRadius);
    const options = {
      shape: shape,
      closeShape: true,
      path: path,
      closePath: true,
      sideOrientation: BABYLON.Mesh.DOUBLESIDE, // TODO: Can we change to DEFAULTSIDE?
      cap: BABYLON.Mesh.CAP_ALL,
      //adjustFrame: true,
      //firstNormal: new BABYLON.Vector3(1, 1, 1),
    };
    /* NOTE, there might be something wrong about my understanding of how the ExtrudeShape
     * works. There are some artifacts in the corners, and possibly some unexpected coloring/shading
     * on the top. Or, I have a bug in the shape/path creation code. 4/10/2023. */
    return BABYLON.MeshBuilder.ExtrudeShape(name, options, scene);
  }
}

/* The original BasketBuilder class was developed in the Babylon Playground:
 * https://playground.babylonjs.com/#EUNZR2#28 */
class BaseBasketBuilder {
  constructor(scene) {
    this.scene = scene;

    let material = this.scene.getMaterialByID('Transparent');
    if (!material) {
      material = new BABYLON.StandardMaterial('Transparent', this.scene);
      material.alpha = 0;
      material.freeze();
    }
    this.transparentMaterial = material;
    // Cached instance we can use for cloning
    this.OriginalBasket = null;
  }

  createVector3Array(points) {
    return points.map(v => new BABYLON.Vector3(v[0], v[1], v[2]));
  }

  createContainerBox(name, rect) {
    const box = BABYLON.MeshBuilder.CreateBox(name, {
      width: rect.width,
      height: rect.height,
      depth: rect.depth,
      //faceUV: faceUV,
      //topBaseAt: options.topBaseAt,
      //bottomBaseAt: options.bottomBaseAt,
      //wrap: true,
      //updatable: true
    }, this.scene);
    box.material = this.transparentMaterial;
    box.isPickable = false;
    return box;
  }

  /**
   * Add multiple clones of the mesh created by the createMesh callback. The position and size of each clone
   * can be varied by using the startRect and endRect.
   */
  addMultipleClones(parent, cloneCount, startRect, endRect, createMesh, isImplicit = false) {
    let divisor = cloneCount - 1;
    if (!isImplicit)
      divisor += 2;
    const div = new BABYLON.Vector3(divisor, divisor, divisor);

    let deltaLoc = endRect.location.subtract(startRect.location);
    deltaLoc = deltaLoc.divide(div);

    let deltaScale = endRect.size.subtract(startRect.size);
    deltaScale = deltaScale.divide(div);

    const location = startRect.location;
    const scale = startRect.size;

    if (!isImplicit) {
      location.addInPlace(deltaLoc);
      scale.addInPlace(deltaScale);
    }

    let original;
    for (let i = 0; i < cloneCount; i++) {
      let clone;
      if (i == 0) {
        clone = original = createMesh();
      } else {
        // If we weren't merging meshes (see addBasket method), then we could've used createInstance instead:
        //clone = original.createInstance(`${original.name} ${i + 1}`);
        clone = original.clone(`${original.name} ${i + 1}`);
      }
      clone.parent = parent;
      clone.position.copyFrom(location);
      clone.scaling.copyFrom(scale);
      clone.freezeWorldMatrix(); // Performance! I think it's safe to do this (but we can't manipulate in Inspector anymore)

      location.addInPlace(deltaLoc);
      scale.addInPlace(deltaScale);
    }
  }
}

export class FlexiBasketBuilder extends BaseBasketBuilder {
  constructor(scene) {
    super(scene);
    this.tubeTesselation = 3;
  }

  /** Helper method for creating a wire mesh given a path, radius, and tesselation. */
  createWire(name, path, radius, material, tessellation, freeze = false) {
    const tube = BABYLON.MeshBuilder.CreateTube(name, {
      path: path,
      radius: radius,
      tessellation: tessellation || this.tubeTesselation,
    }, this.scene);
    tube.material = tube.material || material;
    tube.isPickable = false;
    if (freeze) {
      tube.freezeWorldMatrix();
    }
    return tube;
  }

  addBasket(name, basketWidth, basketDepth, basketHeight, color, scale) {
    /* The wire geometry used in this code was generated by Langlo Designer via the Designer
     * Playground app. If we were to use the 3 dimension parameters above, then we would end up with
     * a partially correct model (some geometry based on the original dimensions and some on the actual
     * dimensions). As a work around we calculate the scale between  the original dimensions and the
     * actual dimensions and apply this scale to the basket container mesh. This work around is more
     * resource efficient since we can reuse a single basket. But, baskets of other sizes than the
     * original will not look exactly like the real world baskets (they're stretched instead of
     * having more/less wires). */
    const origBasketWidth = 40.6;
    const origBasketDepth = 50;
    const origBasketHeight = 15.2;
    const basketScale = new BABYLON.Vector3(
      basketWidth / origBasketWidth, basketHeight / origBasketHeight, basketDepth / origBasketDepth);

    let material = null;
    if (color) {
      material = new BABYLON.StandardMaterial(name + ' material', this.scene);
      material.diffuseColor = BABYLON.Color3.FromHexString(color);
      material.freeze();
    }

    if (this.OriginalBasket) {
      // We can create a cloned basket instead of wasting resources on full new copies
      const clonedBasket = this.OriginalBasket.clone(name);
      clonedBasket.parent = null; // Reset parent
      clonedBasket.position = new BABYLON.Vector3(0, 0, 0); // Reset the position
      // The first "basketScale" scales from the original basket size to the current actual size
      // The second "scale" is used for converting units, i.e. from cm to mm
      if (scale) {
        basketScale.multiplyInPlace(new BABYLON.Vector3(scale, scale, scale));
      }
      clonedBasket.scaling = basketScale;
      const wireMeshes = clonedBasket.getChildMeshes(true);
      wireMeshes[0].material = material;
      return clonedBasket;
    }

    //#region ****** CONSTANTS & DECLARATIONS ******

    const ThinWireRadius = 0.15;
    const StandardWireRadius = 0.3;
    const XAxisWireCount = 14;
    const ZAxisWireCount = 11; //isWideBasket ? 15 : 11;
    const ShortWireCount = 3;
    const XMargin = 1.25;
    const YTopMargin = 1.5;
    const ZFrontMargin = 1.0;
    const topWireCornerLen = 1.0; // The arc is drawn inside a square with each side 1.0 cm long
    const wireCornerLen = 0.5; // The arc is drawn inside a square with each side 0.5 cm long

    const BasketNetWidth = origBasketWidth - (XMargin * 2);
    const BasketNetDepth = origBasketDepth - ZFrontMargin;
    const BasketNetHeight = origBasketHeight - YTopMargin;
    const XAxisOffset = BasketNetWidth / ZAxisWireCount;
    // Offset from the left & right edge to the first & last wire running from front to back on the Z-axis:
    const XAxisOffset2 = XAxisOffset - wireCornerLen;
    // Offset from the front & back edge to the first & last wire running from left to right on the X-axis:
    const ZAxisOffset = BasketNetDepth / XAxisWireCount;
    const ZAxisOffset2 = ZAxisOffset - wireCornerLen;

    // Length of wires running from left to right on the front and back side:
    const XShortWireScale = new BABYLON.Vector3(BasketNetWidth - (XAxisOffset * 2.0), 1.0, 1.0);
    // Length of wires running from front to back on the left and right side:
    const ZShortWireScale = new BABYLON.Vector3(1.0, 1.0, BasketNetDepth - (ZAxisOffset * 2.0));
    const unitSize = BABYLON.Vector3.One();

    //#endregion ****** CONSTANTS & DECLARATIONS ******

    // The basket box is the parent of all the wire meshes:
    const basket = this.createContainerBox(name, { width: origBasketWidth, height: origBasketHeight, depth: origBasketDepth });
    // We'll add all wire meshes to this temp parent before finally merging them.
    const tempParent = new BABYLON.Node('temp');

    //#region ***** Add Wires *****

    // 1. Add wire on the top of the basket; wraps around all sides:
    const topWirePath = this.createVector3Array([[0, 13.7, 1], [0, 13.7, 48], [0.015625, 13.7, 48.15625], [0.125, 13.7, 48.5], [0.421875, 13.7, 48.84375], [1, 13.7, 49], [37.1, 13.7, 49], [37.25625, 13.7, 48.984375], [37.6, 13.7, 48.875], [37.94375, 13.7, 48.578125], [38.1, 13.7, 48], [38.1, 13.7, 1], [38.084375, 13.7, 0.84375], [37.975, 13.7, 0.5], [37.678125, 13.7, 0.15625], [37.1, 13.7, 0], [1, 13.7, 0], [0.84375, 13.7, 0.015625], [0.5, 13.7, 0.125], [0.15625, 13.7, 0.421875], [0, 13.7, 1]]);
    const topWire = this.createWire('TopWire', topWirePath, StandardWireRadius, material);
    topWire.parent = tempParent;

    // 2. Add wires running from left to right on the front side
    let startLocation = new BABYLON.Vector3(XAxisOffset, 0.0, BasketNetDepth - ZAxisOffset2);
    let startSize = XShortWireScale;
    let endLocation = new BABYLON.Vector3(XAxisOffset, BasketNetHeight, BasketNetDepth);
    let endSize = XShortWireScale;
    this.addMultipleClones(tempParent, ShortWireCount,
      { location: startLocation, size: startSize }, { location: endLocation, size: endSize },
      () => {
        const wirePath = this.createVector3Array([[0, 0, 0], [1, 0, 0]]);
        return this.createWire('LeftToRightWire (Front)', wirePath, ThinWireRadius, material);
      });


    // 3. Add wires running from left to right on the back side
    startLocation = new BABYLON.Vector3(XAxisOffset, 0.0, ZAxisOffset2);
    startSize = XShortWireScale;
    endLocation = new BABYLON.Vector3(XAxisOffset, BasketNetHeight, 0.0);
    endSize = XShortWireScale;
    this.addMultipleClones(tempParent, ShortWireCount,
      { location: startLocation, size: startSize }, { location: endLocation, size: endSize },
      () => {
        const wirePath = this.createVector3Array([[0, 0, 0], [1, 0, 0]]);
        return this.createWire('LeftToRightWire (Back)', wirePath, ThinWireRadius, material);
      });


    // 4. Add wires running from front to back on the left side
    startLocation = new BABYLON.Vector3(XAxisOffset2, 0.0, ZAxisOffset);
    startSize = ZShortWireScale;
    endLocation = new BABYLON.Vector3(0.0, BasketNetHeight, ZAxisOffset);
    endSize = ZShortWireScale;
    this.addMultipleClones(tempParent, ShortWireCount,
      { location: startLocation, size: startSize }, { location: endLocation, size: endSize },
      () => {
        const wirePath = this.createVector3Array([[0, 0, 0], [0, 0, 1]]);
        return this.createWire('FrontToBackWire (Left)', wirePath, ThinWireRadius, material);
      });


    // 5. Add wires running from front to back on the right side
    startLocation = new BABYLON.Vector3(BasketNetWidth - XAxisOffset2, 0.0, ZAxisOffset);
    startSize = ZShortWireScale;
    endLocation = new BABYLON.Vector3(BasketNetWidth, BasketNetHeight, ZAxisOffset);
    endSize = ZShortWireScale;
    this.addMultipleClones(tempParent, ShortWireCount,
      { location: startLocation, size: startSize }, { location: endLocation, size: endSize },
      () => {
        const wirePath = this.createVector3Array([[0, 0, 0], [0, 0, 1]]);
        return this.createWire('FrontToBackWire (Right)', wirePath, ThinWireRadius, material);
      });


    // 6. Add base wires running from left to right (on X-axis)
    startLocation = new BABYLON.Vector3(0.0, 0.0, ZAxisOffset);
    startSize = unitSize;
    endLocation = new BABYLON.Vector3(0.0, 0.0, BasketNetDepth - ZAxisOffset);
    endSize = unitSize;
    this.addMultipleClones(tempParent, XAxisWireCount,
      { location: startLocation, size: startSize }, { location: endLocation, size: endSize },
      () => {
        const wirePath = this.createVector3Array([[0, 13.7, 0], [2.96363636363636, 0.5, 0], [2.97144886363636, 0.421875, 0], [3.02613636363636, 0.25, 0], [3.17457386363636, 0.078125, 0], [3.46363636363636, 0, 0], [34.6363636363636, 0, 0], [34.9254261363636, 0.078125, 0], [35.0738636363636, 0.25, 0], [35.1285511363636, 0.421875, 0], [35.1363636363636, 0.5, 0], [38.1, 13.7, 0]]);
        return this.createWire('X-axis Wire', wirePath, ThinWireRadius, material);
      }, true);


    // 7. Add base wires running from front to back (on Z-axis)
    startLocation = new BABYLON.Vector3(XAxisOffset, 0.0, 0.0);
    startSize = unitSize;
    endLocation = new BABYLON.Vector3(BasketNetWidth - XAxisOffset, 0.0, 0.0);
    endSize = unitSize;
    this.addMultipleClones(tempParent, ZAxisWireCount,
      { location: startLocation, size: startSize }, { location: endLocation, size: endSize },
      () => {
        const wirePath = this.createVector3Array([[0, 13.7, 0], [0, 0.5, 3], [0, 0.421875, 3.0078125], [0, 0.25, 3.0625], [0, 0.078125, 3.2109375], [0, 0, 3.5], [0, 0, 45.5], [0, 0.0078125, 45.578125], [0, 0.0625, 45.75], [0, 0.2109375, 45.921875], [0, 0.5, 46], [0, 13.7, 49]]);
        return this.createWire('Z-axis Wire', wirePath, ThinWireRadius, material);
      }, true);

    //#endregion ***** Add Wires *****

    // Performance optimization: merge all the child wire meshes:
    const childMeshes = tempParent.getChildMeshes(true);
    const wireMesh = BABYLON.Mesh.MergeMeshes(childMeshes);
    wireMesh.name = 'Wires';
    wireMesh.parent = basket;
    tempParent.dispose();

    /* The purpose of the code below is to move the wires such that they are centered inside the parent basket box.
     * Most Babylon meshes have their local origin set to the center of their bounds, but tubes's local origin
     * are set to the bottom/left/front of the bounds. */
    wireMesh.position.x = -(origBasketWidth / 2) + XMargin;
    wireMesh.position.y = -origBasketHeight / 2;
    wireMesh.position.z = -(origBasketDepth / 2) + ZFrontMargin;

    // The first "basketScale" scales from the original basket size to the current actual size
    // The second "scale" is used for converting units, i.e. from cm to mm
    if (scale) {
      basketScale.multiplyInPlace(new BABYLON.Vector3(scale, scale, scale))
    }
    basket.scaling = basketScale;
    this.OriginalBasket = basket;
    return basket;
  }
}

/**
 * Creates a mesh basket including its top rim using the QuadriLateralFrustumBuilder and
 * BasketRimBuilder helper classes.
 *
 * This class was developed as a part of the prototyping of our mesh basket here:
 * https://playground.babylonjs.com/#WVLDLM#35
 */
export class MeshBasketBuilder extends BaseBasketBuilder {
  constructor(scene) {
    super(scene);
  }

  /* Obsolete, only used for testing!!!
  createBasketUsingBox(name, width, height, depth, scene, material) {
    //const widthRatio = component.getMaterialWidthRatio();
    //const heightRatio = component.getMaterialHeightRatio();
    //const depthRatio = component.getMaterialDepthRatio();
    let faceUV = [];
    faceUV[0] = new BABYLON.Vector4(0.0, 0.0, 1.0, 1.0); // Rear face
    faceUV[1] = new BABYLON.Vector4(0.0, 0.0, 1.0, 1.0); // Front face
    faceUV[2] = new BABYLON.Vector4(0.0, 0.0, 1.0, 1.0); // Right side
    faceUV[3] = new BABYLON.Vector4(0.0, 0.0, 1.0, 1.0); // Left side
    faceUV[4] = new BABYLON.Vector4(0.0, 0.0, 0, 0); // On top
    faceUV[5] = new BABYLON.Vector4(0.0, 0.0, 1.0, 1.0); // At bottom

    const mesh = BABYLON.MeshBuilder.CreateBox(name, {
      width: width,
      height: height,
      depth: depth,
      sideOrientation: BABYLON.Mesh.DOUBLESIDE,
      //faceUV: faceUV,
      frontUV: faceUV,
      backUV: faceUV,
      //topBaseAt: options.topBaseAt,
      //bottomBaseAt: options.bottomBaseAt,
      //wrap: false,
      updatable: true
    }, scene);
    return mesh;
  }
  */

  /**
   * Creates a 3D basket shape. This shape can be looked at as a trapezoidal prism, and is also
   * called a quadrilateral frustum or a square frustum. The basket shape is created by using
   * a custom extrude function. "Extrude" here means defining a 2D shape and then "duplicating"
   * it on a defined path. We'll define a square in the XY plane, "duplicate" it on a straight
   * line path in the XZ direction, and finally scale it to our millimeter dimensions, rotate
   * it 90 degrees to make the open end of the basket be on top, and move it into our preferred
   * position.
   * This function was developed in the Babylon Playground (https://playground.babylonjs.com/#AUL9SE#8), 4/6/2023.
   **/
  /* Obsolete, only used for testing
  createBasketUsingExtrudeShape(name, width, height, depth, scene, material) {
    // First, define a unit square in the XY plane with 0,0 as the center:
    const rectangleShape = [
      new BABYLON.Vector3(-0.5, -0.5, 0), // bottom left
      new BABYLON.Vector3(-0.5, 0.5, 0),  // top left
      new BABYLON.Vector3(0.5, 0.5, 0),   // top right
      new BABYLON.Vector3(0.5, -0.5, 0),  // bottom right
    ];
    rectangleShape.push(rectangleShape[0]);  // close profile
    // Extrusion path, in unit size:
    const path = [new BABYLON.Vector3(0, 0, 0), new BABYLON.Vector3(0, 0, 1)];

    // Scale the extruded square to make the trapezoidal shape:
    const scaleFunc = function (i, distance) {
      if (i === 0) return 1.0;
      //  bottom width (mm) / top width (mm) = scale between bottom and top width of basket
      return 340 / 405;
    }

    let mesh = BABYLON.MeshBuilder.ExtrudeShapeCustom(name,
      {
        shape: rectangleShape,
        path: path,
        scaleFunction: scaleFunc,
        sideOrientation: BABYLON.Mesh.DOUBLESIDE, // We want to see the inside
        //cap: BABYLON.Mesh.CAP_END, // Covers the bottom of the shape
      },
      scene);
    // Scale to the actual dimensions of the basket (in decimeter)
    mesh.scaling = new BABYLON.Vector3(width, depth, height);
    // Rotate 90 degrees around X-axis because extrusions are done in the Z-axis direction:
    mesh.rotation.x = BABYLON.Tools.ToRadians(90);

    // Position the basket such that its bottom/left/front is at 0,0,0. Note,
    // in Langlo Store we might need to align to the back instead of the front.
    mesh.position.x = width / 2;
    mesh.position.y = height;
    mesh.position.z = depth / 2;

    if (material) {
      mesh.material = material;
    }

    return mesh;
  }
  */

  /**
   * Flips the various UV faces such that their orientation is correct for a quadrilateral frustum
   * shape created with the BABYLON.MeshBuilder.CreatePolyHedron function.
   */
  reOrientBoxUVSprites(faceUVHelper, faceUV) {
    faceUVHelper.flipUVOverYAxis(faceUV, QLFFaceNdx.front);
    faceUVHelper.flipUVOverXAxis(faceUV, QLFFaceNdx.back);
    faceUVHelper.flipUVOverXAxis(faceUV, QLFFaceNdx.left);
    faceUVHelper.flipUVOverYAxis(faceUV, QLFFaceNdx.right);
    faceUVHelper.flipUVOverYAxis(faceUV, QLFFaceNdx.top);
    faceUVHelper.flipUVOverYAxis(faceUV, QLFFaceNdx.bottom);
  }

  /**
   * Creates a mesh basket including its top rim using the QuadriLateralFrustumBuilder and
   * BasketRimBuilder helper classes. This method was developed as a part of the prototyping of our
   * mesh basket here: https://playground.babylonjs.com/#WVLDLM#35
   */
  createBasketUsingQuadriLateralFrustum(name, width, height, depth, basketMaterial, rimMaterial, scene) {
    const rimDimensions = {
      basketWidth: width,
      basketDepth: depth,
      yCornerRadius: 5,
      edgeWidth: 10,
      edgeHeight: 2.5,
      zCornerRadius: 0.5,
    };
    const rimOrigin = { x: 0, y: height, z: 0 };

    const rimBuilder = new BasketRimBuilder();
    const rim = rimBuilder.createMesh('Rim', rimOrigin, rimDimensions, scene);
    rim.isPickable = false;
    if (rimMaterial) {
      rim.material = rimMaterial;
    }

    /* Define the quadrilateral frustum parameters. This code assumes the rim will be
     * positioned with half of it outside the top edge of the basket part, and the other
     * half of the rim inside the basket. It's possible that the entire rim should be
     * placed outside the basket, we'll have to revisit this after seeing the basket in
     * person. 4/10/2023. */
    const topBotRatio = 340 / 405; // Known top and bottom width of our mesh basket i mm.
    // Define the quadrilateral frustum parameters.
    const basketDimensions = {
      topWidth: width - rimDimensions.edgeWidth,
      botWidth: (width - rimDimensions.edgeWidth) * topBotRatio,
      topDepth: depth - rimDimensions.edgeWidth,
      botDepth: (depth - rimDimensions.edgeWidth) * topBotRatio,
      height: height,
    };
    const basketOrigin = {
      x: (rimDimensions.edgeWidth / 2),
      y: 0,
      z: (rimDimensions.edgeWidth / 2),
    };

    let useFullImageOnEachSprite;
    const useImageWithSprites = false;
    if (useImageWithSprites) {
      // The texture's image should be a rectangle with 6 sprites on 1 row.
      useFullImageOnEachSprite = false;
    } else {
      // The image has a single sprite used on every face of the frustum.
      useFullImageOnEachSprite = true;
    }
    const faceUVHelper = new FaceUVHelper();
    const faceUV = faceUVHelper.setupFaceUV(6, 1, useFullImageOnEachSprite);
    this.reOrientBoxUVSprites(faceUVHelper, faceUV);

    const invisibleFaces = [];
    invisibleFaces[QLFFaceNdx.top] = true;

    const basketBuilder = new QuadriLateralFrustumBuilder();
    const basket = basketBuilder.createMesh('Basket', basketOrigin, basketDimensions, faceUV,
      /*faceColors:*/ null, invisibleFaces, scene);
    basket.isPickable = false;
    if (basketMaterial) {
      basket.material = basketMaterial;
    }
    /* This code can be used to "fill" the edges of the basket. If we decide to proceed, then we
     * need to come up with ideal edgesWidth value and colors for the different basket finishes. */
    //basket.enableEdgesRendering();
    //basket.edgesWidth = 80.0;
    //basket.edgesColor = new BABYLON.Color4(0.8, 0.8, 0.8); // A silver variation

    const containerRect = {
      width: width,
      height: height,
      depth: depth,
    };

    const basketWithRim = this.createContainerBox(name, containerRect);
    basket.parent = basketWithRim;
    rim.parent = basketWithRim;

    /* The 2 child meshes (basket and rim) have their left/bottom/front aligned with the world's
     * left/bottom/front. The basketWithRim container box, on the other hand, has its origin in the
     * middle of the box which means the box is centered round the world's (0,0,0). The purpose of
     * the next few lines is to move the 2 child meshes so that they are positioned relative to the
     * parent basketWithRim. If all child and parent meshes had their origin at the world (0,0,0),
     * then this code wouldn't be needed. 4/18/2023. */
    const lbfOffset = new BABYLON.Vector3(containerRect.width / 2, containerRect.height / 2, containerRect.depth / 2);
    basket.position.subtractInPlace(lbfOffset);
    rim.position.subtractInPlace(lbfOffset);
    /* The basketWithRim including child meshes is now centered around the world's (0,0,0). If we want to
     * align the basket's left/bottom/front with the world's left/bottom/front, then enable the next line: */
    //basketWithRim.position.addInPlace(lbfOffset);
    //basketWithRim.showBoundingBox = true;
    return basketWithRim;
  }

  addBasket(name, compRect) {
    //const basket = this.createBasketUsingExtrudeShape(name, compRect.width, compRect.height, compRect.depth, this.scene);
    //const basket = this.createBasketUsingBox(name, compRect.width, compRect.height, compRect.depth, this.scene);
    const basket = this.createBasketUsingQuadriLateralFrustum(name,
      compRect.width, compRect.height, compRect.depth, null, null, this.scene);
    basket.isPickable = false;
    return basket;
  }
}
